import type { FeedbackAnswerQuestion, QuestionData, QuestionExportAnswer } from '@repo/types/questions/Question';
import type { QuizSection } from '@repo/types/quiz';

import { QuestionFactory } from '@repo/lib/quiz/questions/class/QuestionFactory';

import { Element } from '@/components/ui/scroll';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { quizExtensionSet } from '@/lib/tiptap-extensions-sets';

import { QuestionMarkedType } from '.';

interface SectionMarkedProps {
  section: QuizSection;
  idx: number;
  marked?: FeedbackAnswerQuestion[][];
  answers?: QuestionExportAnswer[][];
  showOnlyNotMarked?: boolean;
  onEditMark?: (data: Partial<FeedbackAnswerQuestion>, sectionIdx: number, questionIdx: number) => void;
}

export function SectionMarked(sectionMarkedProps: SectionMarkedProps) {
  const { section, marked, answers, idx, onEditMark, showOnlyNotMarked } = sectionMarkedProps;

  const isQuestionNeedsMarking = (questionData: QuestionData, questionMarked: FeedbackAnswerQuestion | undefined, userAnswer: QuestionExportAnswer | undefined): boolean => {
    const questionFactory = new QuestionFactory();
    const qClass = questionFactory.createQuestion(questionData, null, questionMarked);

    if (userAnswer) qClass.importAnswer(userAnswer);

    return qClass.correctState() === 'waiting';
  };

  const shouldDisplayQuestion = (qIdx: number): boolean => {
    const questionData = section.questions?.[qIdx];
    const questionMarked = marked?.[idx]?.[qIdx];
    const userAnswer = answers?.[idx]?.[qIdx];

    if (!questionData || !userAnswer) return false;

    if (!showOnlyNotMarked) return true;

    return isQuestionNeedsMarking(questionData, questionMarked, userAnswer);
  };

  const visibleQuestions = section.questions.map((question, qIdx) => ({ question, qIdx })).filter(({ qIdx }) => shouldDisplayQuestion(qIdx));

  return (
    <div className="md:bg-background w-full md:rounded-2xl md:border md:p-4">
      <div className="mb-8 space-y-1">
        <h5 className="text-foreground text-xl font-semibold">{section.name}</h5>
        <div className="text-accent-foreground text-sm">
          <Tiptap extensions={quizExtensionSet} editable={false} content={section.content} />
        </div>
      </div>
      <div className="flex grow flex-col gap-4">
        {visibleQuestions.length === 0 && (
          <div className="text-muted-foreground my-4 text-sm">
            <p className="text-center">Brak pytań do wyświetlenia w tej sekcji. Spróbuj zmienić filtry.</p>
          </div>
        )}
        {visibleQuestions.map(({ question, qIdx: originalQIdx }) => {
          const questionMarked = marked?.[idx]?.[originalQIdx];
          const questionAnswer = answers?.[idx]?.[originalQIdx];

          return (
            <Element key={`${idx}/${originalQIdx}`} name={`section-${idx}/question-${originalQIdx}`} asChild>
              <div className="space-y-2">
                <p className="text-muted-foreground mx-2 text-sm">
                  Pytanie {idx + 1}.{originalQIdx + 1}
                </p>
                <QuestionMarkedType
                  onEditMark={onEditMark ? (data) => onEditMark(data, idx, originalQIdx) : undefined}
                  key={`${idx}-${originalQIdx}`}
                  marked={questionMarked}
                  answer={questionAnswer}
                  question={question}
                />
              </div>
            </Element>
          );
        })}
      </div>
    </div>
  );
}
